import { ChainIds } from "@umami/chains";
import {
  Contract,
  type ContractTransaction,
  JsonRpcProvider,
  type Provider,
  type Signer,
  ZeroAddress,
  ethers,
} from "ethers";

import { type Network as BridgeNetwork, config, ierc20Abi } from "./config";
import { originalTokenBridgeAbi } from "./original-token-bridge-abi";
import { wrappedTokenBridgeAbi } from "./wrapped-token-bridge-abi";

export enum BridgeDirection {
  EthereumToEtherlink = "ethereumToEtherlink",
  EthereumToBsc = "ethereumToBsc",
  TezosToEtherlink = "tezosToEtherlink",
  EtherlinkToTezos = "etherlinkToTezos",
  EtherlinkToEthereum = "etherlinkToEthereum",
  EtherlinkToBsc = "etherlinkToBsc",
  EtherlinkToEtherlink = "etherlinkToEtherlink",
  BscToEthereum = "bscToEthereum",
  BscToEtherlink = "bscToEtherlink",
}

const adapterParams = ethers.solidityPacked(["uint16", "uint256"], [1, 200000]);

/**
 * Arguments for constructing a Bridge instance.
 */
export interface BridgeConstructorArgs {
  /** Network to use (e.g., 'shadownet' or 'mainnet'). */
  network?: BridgeNetwork;
}

/**
 * Arguments for the approveToken method.
 */
export interface ApproveTokenArgs {
  /** The direction of the bridge operation. */
  direction: BridgeDirection;
  /** The address of the token to approve. */
  tokenAddress: string;
  /** The amount of the token to approve. */
  amount: bigint;
  /** The signer to use for the approval transaction. */
  signer: Signer;
}

/**
 * Arguments for the estimateFee method.
 */
export interface EstimateFeeArgs {
  /** The direction of the bridge operation. */
  direction: BridgeDirection;
  /** Whether to use ZRO for fee payment. */
  useZro?: boolean;
}

/**
 * Arguments for the bridgeToken method.
 */
export interface BridgeTokenArgs {
  /** The direction of the bridge (EthereumToEtherlink or EtherlinkToEthereum). */
  direction: BridgeDirection;
  /** The address of the token to bridge. */
  tokenAddress: string;
  /** The amount of the token to bridge. */
  amount: bigint;
  /** The recipient address on the destination chain. */
  recipient: string;
  /** Whether the token is native ETH (Ethereum to Etherlink only). */
  isNativeEth?: boolean;
  /** Whether to unwrap WETH on the destination (Etherlink to Ethereum only). */
  unwrapWeth?: boolean;
  /** The signer to use for the bridge transaction. */
  signer: Signer;
}

export class Bridge {
  /** The network configuration in use. */
  network: BridgeNetwork;
  /** Ethers provider for Ethereum network. */
  ethereumProvider: JsonRpcProvider;
  /** Ethers provider for Etherlink network. */
  etherlinkProvider: JsonRpcProvider;
  /** Ethers provider for BSC network. */
  bscProvider: JsonRpcProvider;
  /** Contract instance for the OriginalTokenBridge on Ethereum. */
  originalTokenBridgeContract: Contract;
  /** Contract instance for the WrappedTokenBridge on Etherlink. */
  wrappedTokenBridgeContract: Contract;
  /** Contract instance for the Bridge on BSC. */
  bscBridgeContract: Contract;
  /** WETH token address on Ethereum. */
  ethereumWethAddress: string;
  /** LayerZero chain ID for Ethereum. */
  ethereumLayerZeroChainId: number;
  /** LayerZero chain ID for Etherlink. */
  etherlinkLayerZeroChainId: number;
  /** LayerZero chain ID for BSC. */
  bscLayerZeroChainId: number;
  /** WrappedTokenBridge contract address on Etherlink. */
  wrappedTokenBridgeContractAddress: string;
  /** OriginalTokenBridge contract address on Ethereum. */
  originalTokenBridgeContractAddress: string;
  /** Bridge contract address on BSC. */
  bscBridgeContractAddress: string;

  /**
   * Constructs a new Bridge instance for cross-chain token bridging.
   */
  constructor({ network = "shadownet" }: BridgeConstructorArgs = {}) {
    this.network = network;
    const cfg = config[network];

    this.ethereumProvider = new JsonRpcProvider(cfg.ethereumRpcUrl);
    this.etherlinkProvider = new JsonRpcProvider(cfg.etherlinkRpcUrl);
    this.bscProvider = new JsonRpcProvider(cfg.bscRpcUrl);

    // Set contract addresses and chain IDs
    this.ethereumWethAddress = cfg.ethereumWethAddress;
    this.ethereumLayerZeroChainId = Number(cfg.ethereumLayerZeroChainId);
    this.etherlinkLayerZeroChainId = Number(cfg.etherlinkLayerZeroChainId);
    this.bscLayerZeroChainId = Number(cfg.bscLayerZeroChainId);
    this.wrappedTokenBridgeContractAddress = cfg.wrappedTokenBridgeContractAddress;
    this.originalTokenBridgeContractAddress = cfg.originalTokenBridgeContractAddress;
    this.bscBridgeContractAddress = cfg.bscBridgeContractAddress;

    // Initialize contracts without signers for read-only operations
    this.originalTokenBridgeContract = new Contract(
      cfg.originalTokenBridgeContractAddress,
      originalTokenBridgeAbi,
      this.ethereumProvider
    );
    this.wrappedTokenBridgeContract = new Contract(
      cfg.wrappedTokenBridgeContractAddress,
      wrappedTokenBridgeAbi,
      this.etherlinkProvider
    );
    this.bscBridgeContract = new Contract(
      cfg.bscBridgeContractAddress,
      originalTokenBridgeAbi,
      this.bscProvider
    );
  }

  /**
   * Returns an ERC20 contract instance for the given address and signer/provider.
   */
  getErc20Contract(tokenAddress: string, signerOrProvider: Provider | Signer) {
    return new Contract(tokenAddress, ierc20Abi, signerOrProvider);
  }

  /**
   * Prepares the approval transaction for the bridge contract to spend a specified amount of a token.
   * Returns the transaction to be signed by the UI.
   *
   * @param params - The parameters for approving a token.
   * @returns The transaction to be signed, or null if no approval is needed.
   */
  async prepareApproveToken({
    direction,
    tokenAddress,
    amount,
    signer,
  }: ApproveTokenArgs): Promise<ContractTransaction | null> {
    try {
      // Skip approval for native ETH (zero address) - no approval needed for native tokens
      if (tokenAddress === "0x0000000000000000000000000000000000000000") {
        return null;
      }

      // Determine the spender address based on the bridge direction
      let spender: string;
      switch (direction) {
        case BridgeDirection.EthereumToEtherlink:
        case BridgeDirection.EthereumToBsc:
          spender = this.originalTokenBridgeContractAddress;
          break;
        case BridgeDirection.BscToEthereum:
        case BridgeDirection.BscToEtherlink:
          spender = this.bscBridgeContractAddress;
          break;
        case BridgeDirection.EtherlinkToEthereum:
        case BridgeDirection.EtherlinkToBsc:
        case BridgeDirection.EtherlinkToTezos:
          spender = this.wrappedTokenBridgeContractAddress;
          break;
        case BridgeDirection.TezosToEtherlink:
        case BridgeDirection.EtherlinkToEtherlink:
          throw new Error(`Unsupported bridge direction: ${direction}`);
        default:
          throw new Error(`Unsupported bridge direction: ${direction}`);
      }

      // Determine the correct provider based on the bridge direction
      let provider: Provider;
      switch (direction) {
        case BridgeDirection.EthereumToEtherlink:
        case BridgeDirection.EthereumToBsc:
          provider = this.ethereumProvider;
          break;
        case BridgeDirection.BscToEthereum:
        case BridgeDirection.BscToEtherlink:
          provider = this.bscProvider;
          break;
        case BridgeDirection.EtherlinkToEthereum:
        case BridgeDirection.EtherlinkToBsc:
        case BridgeDirection.EtherlinkToTezos:
          provider = this.etherlinkProvider;
          break;
        default:
          throw new Error(`Unsupported bridge direction: ${direction}`);
      }

      const erc20Contract = this.getErc20Contract(tokenAddress, provider);
      const userAddress = await signer.getAddress();
      const allowance = await erc20Contract.allowance(userAddress, spender);

      // Only prepare approval if allowance is insufficient
      if (allowance < amount) {
        return erc20Contract.approve.populateTransaction(spender, amount);
      }

      return null;
    } catch (error) {
      console.error(`Error preparing token approval for ${direction}:`, error);
      throw error;
    }
  }

  /**
   * Estimates the fee required for bridging a token in the specified direction.
   * Returns the native and ZRO fee amounts.
   *
   * @param params - The parameters for estimating the fee.
   * @returns The estimated native and ZRO fee amounts.
   */
  async estimateFee({ direction, useZro = false }: EstimateFeeArgs) {
    try {
      if (
        direction === BridgeDirection.EthereumToEtherlink ||
        direction === BridgeDirection.EthereumToBsc
      ) {
        const [nativeFee, zroFee] = await this.originalTokenBridgeContract.estimateBridgeFee(
          useZro,
          adapterParams
        );

        return { nativeFee, zroFee };
      } else if (
        direction === BridgeDirection.BscToEthereum ||
        direction === BridgeDirection.BscToEtherlink
      ) {
        const [nativeFee, zroFee] = await this.bscBridgeContract.estimateBridgeFee(
          useZro,
          adapterParams
        );

        return { nativeFee, zroFee };
      } else {
        let dstChainId: number;
        switch (direction) {
          case BridgeDirection.EtherlinkToEthereum:
            dstChainId = this.ethereumLayerZeroChainId;
            break;
          case BridgeDirection.EtherlinkToBsc:
            dstChainId = this.bscLayerZeroChainId;
            break;
          case BridgeDirection.TezosToEtherlink:
          case BridgeDirection.EtherlinkToEtherlink:
          case BridgeDirection.EtherlinkToTezos:
            throw new Error(`Unsupported bridge direction: ${direction}`);
          default:
            throw new Error(`Unsupported bridge direction: ${direction}`);
        }

        const [nativeFee, zroFee] = await this.wrappedTokenBridgeContract.estimateBridgeFee(
          dstChainId,
          useZro,
          adapterParams
        );

        return { nativeFee, zroFee };
      }
    } catch (error) {
      console.error(`Error estimating fee for ${direction}:`, error);
      throw error;
    }
  }

  /**
   * Checks if the bridge contract has enough tokens to bridge.
   * @param tokenAddress - The address of the token to check
   * @param amount - The amount to bridge
   * @returns true if the bridge has enough tokens, false otherwise
   */
  async hasEnoughTokens(tokenAddress: string, amount: bigint): Promise<boolean> {
    try {
      // Skip balance check for native ETH (zero address) - native tokens don't need this check
      if (tokenAddress === "0x0000000000000000000000000000000000000000") {
        return true;
      }

      const erc20Contract = this.getErc20Contract(tokenAddress, this.etherlinkProvider);
      const balance = await erc20Contract.balanceOf(this.wrappedTokenBridgeContractAddress);

      return balance >= amount;
    } catch (error) {
      console.error("Error checking bridge contract balance:", error);
      throw error;
    }
  }

  /**
   * Prepares the bridge transaction to be signed by the UI.
   * Handles both native ETH and ERC20 tokens.
   *
   * @param params - The parameters for bridging a token.
   * @returns The transaction to be signed.
   */
  async prepareBridgeToken({
    direction,
    tokenAddress,
    amount,
    recipient,
    isNativeEth = false,
    unwrapWeth = false,
    signer,
  }: BridgeTokenArgs): Promise<ContractTransaction> {
    try {
      const callParams = {
        refundAddress: await signer.getAddress(),
        zroPaymentAddress: ZeroAddress,
      };
      const useZro = false;
      let bridgeFee: bigint;
      let dstChainId: string;

      // Determine destination chain ID based on direction
      switch (direction) {
        case BridgeDirection.EthereumToEtherlink:
        case BridgeDirection.BscToEtherlink:
          dstChainId = this.etherlinkLayerZeroChainId.toString();
          break;
        case BridgeDirection.EtherlinkToEthereum:
        case BridgeDirection.BscToEthereum:
          dstChainId = this.ethereumLayerZeroChainId.toString();
          break;
        case BridgeDirection.EthereumToBsc:
        case BridgeDirection.EtherlinkToBsc:
          dstChainId = this.bscLayerZeroChainId.toString();
          break;
        case BridgeDirection.TezosToEtherlink:
        case BridgeDirection.EtherlinkToEtherlink:
        case BridgeDirection.EtherlinkToTezos:
          throw new Error(`Unsupported bridge direction: ${direction}`);
        default:
          throw new Error(`Unsupported bridge direction: ${direction}`);
      }

      if (
        direction === BridgeDirection.EthereumToEtherlink ||
        direction === BridgeDirection.EthereumToBsc
      ) {
        bridgeFee = (
          await this.originalTokenBridgeContract.estimateBridgeFee(useZro, adapterParams)
        )[0];

        if (isNativeEth) {
          return this.originalTokenBridgeContract.bridgeNative.populateTransaction(
            amount,
            recipient,
            callParams,
            adapterParams,
            { value: amount + bridgeFee }
          );
        } else {
          return this.originalTokenBridgeContract.bridge.populateTransaction(
            tokenAddress,
            amount,
            recipient,
            callParams,
            adapterParams,
            { value: bridgeFee }
          );
        }
      } else if (
        direction === BridgeDirection.BscToEthereum ||
        direction === BridgeDirection.BscToEtherlink
      ) {
        bridgeFee = (await this.bscBridgeContract.estimateBridgeFee(useZro, adapterParams))[0];

        if (isNativeEth) {
          return this.bscBridgeContract.bridgeNative.populateTransaction(
            amount,
            recipient,
            callParams,
            adapterParams,
            { value: amount + bridgeFee }
          );
        } else {
          return this.bscBridgeContract.bridge.populateTransaction(
            tokenAddress,
            amount,
            recipient,
            callParams,
            adapterParams,
            { value: bridgeFee }
          );
        }
      } else if (
        direction === BridgeDirection.EtherlinkToEthereum ||
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
        direction === BridgeDirection.EtherlinkToBsc
      ) {
        bridgeFee = (
          await this.wrappedTokenBridgeContract.estimateBridgeFee(dstChainId, useZro, adapterParams)
        )[0];

        return this.wrappedTokenBridgeContract.bridge.populateTransaction(
          tokenAddress,
          dstChainId,
          amount,
          recipient,
          unwrapWeth,
          callParams,
          adapterParams,
          { value: bridgeFee }
        );
      } else {
        throw new Error(`5Unsupported bridge direction: ${direction}`);
      }
    } catch (error) {
      console.error(`Error preparing bridge transaction for ${direction}:`, error);
      throw error;
    }
  }

  /**
   * Gets the appropriate provider based on the chain ID.
   */
  getProvider(chainId: string): Provider {
    switch (chainId.toLowerCase()) {
      case ChainIds.ETHEREUM.toLowerCase():
        return this.ethereumProvider;
      case ChainIds.ETHERLINK.toLowerCase():
        return this.etherlinkProvider;
      case ChainIds.BSC.toLowerCase():
        return this.bscProvider;
      default:
        throw new Error(`Unsupported chain ID: ${chainId}`);
    }
  }
}
